/* Cycling Upgrades — website chat (snippets/cu-chat.liquid). The only file that calls
   /apps/garage/chat/* (the Garage app proxy to the CRM); the browser keeps just the
   session id in localStorage. What the panel shows comes from cu-chat-core.js (unit-tested). */
const chatRoot = document.querySelector('[data-cu-chat]');
// The core module's URL comes from asset_url (with Shopify's ?v= cache-buster),
// so a deploy never pairs this file with a stale cached core.
const core = chatRoot ? await import(chatRoot.dataset.core) : null;
if (chatRoot) init(chatRoot);

async function init(root) {
  const { initialState, reduce, view, canSend, keepPolling, pollCadence, storedSession, launcherLabel, applyConfig } = core;
  const API = '/apps/garage/chat';
  const KEY = 'cu-chat';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel) => root.querySelector(sel);
  const launcher = $('[data-cu-chat-open]');
  const badge = $('[data-cu-chat-unread]');
  const labelEl = $('[data-cu-chat-label]');
  const greetingEl = $('[data-cu-chat-greeting]');
  const panel = $('#cu-chat-panel');
  const closeBtn = $('[data-cu-chat-close]');
  const identify = $('[data-cu-chat-identify]');
  const thread = $('[data-cu-chat-thread]');
  const note = $('[data-cu-chat-note]');
  const compose = $('[data-cu-chat-compose]');
  const area = compose.querySelector('textarea');
  const sendBtn = compose.querySelector('button');

  const COPY = {
    waiting: 'Someone will reply here in a moment.',
    email: (s) => `We'll reply by email at ${s.email || 'the address you gave'}.`,
    limited: 'Slow down a little. Try again in a minute.',
    unavailable: 'Chat is unavailable right now. Please email us instead.',
    failed: "That didn't send. Your message is still in the box; try again.",
  };

  let state = initialState({ email: root.dataset.email, name: root.dataset.name });
  let unavailable = false; // 401/403/503: proxy signature refused or chat off on the CRM side; stop calling it
  let sendError = '';
  let busy = false;

  // The CRM decides whether chat shows and what it says. The snippet's text is only the
  // placeholder; any failure (403 off, 503, network) leaves chat hidden. Plain fetch, not
  // api(): its 404/429/403 handling dispatches into the thread state.
  const cfg = await fetch(API + '/config', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null)).catch(() => null);
  state = applyConfig({ ...state, greeting: greetingEl.textContent.trim(), launcherLabel: labelEl.textContent.trim() }, cfg);
  if (!state.enabled) { console.warn('[cu-chat] chat is off'); return; }
  greetingEl.textContent = state.greeting;
  labelEl.textContent = state.launcherLabel;

  try {
    // since stays null so the first poll restores the whole thread; an expired session is dropped.
    const session = storedSession(JSON.parse(localStorage.getItem(KEY) || 'null'), Date.now());
    if (session) state = { ...state, session, phase: 'thread' };
    else localStorage.removeItem(KEY);
  } catch {}
  const persist = () => {
    try {
      if (state.session) localStorage.setItem(KEY, JSON.stringify({ session: state.session, since: state.since, savedAt: Date.now() }));
      else localStorage.removeItem(KEY);
    } catch {}
  };
  const dispatch = (e) => { state = reduce(state, e); persist(); render(); };

  async function api(path, payload) {
    const r = await fetch(API + path, payload
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) }
      : { headers: { Accept: 'application/json' } });
    const j = await r.json().catch(() => ({}));
    if (r.status === 404) { dispatch({ type: 'unknown' }); throw new Error('unknown'); }
    if (r.status === 429) { dispatch({ type: 'limited', now: Date.now() }); throw new Error('limited'); }
    if (r.status === 401 || r.status === 403 || r.status === 503) { unavailable = true; clearTimeout(timer); render(); throw new Error('unavailable'); }
    if (!r.ok) throw new Error(j.error || 'failed');
    return j;
  }

  // Thread: append new bubbles and re-key the local echo when its server row lands,
  // so the polite live region announces each message once.
  function bubble(m) {
    const el = document.createElement('p');
    el.className = `cu-chat__msg cu-chat__msg--${m.direction === 'out' ? 'staff' : 'you'}`;
    const who = document.createElement('span');
    who.className = 'visually-hidden';
    who.textContent = m.direction === 'out' ? 'Cycling Upgrades: ' : 'You: ';
    el.append(who, document.createTextNode(m.text));
    el.dataset.id = m.id;
    return el;
  }
  function renderThread() {
    const existing = [...thread.children];
    const byId = new Map(existing.map((el) => [el.dataset.id, el]));
    const used = new Set();
    const els = state.messages.map((m) => {
      let el = byId.get(m.id);
      if (!el && m.direction === 'in') {
        el = existing.find((x) => !used.has(x) && x.dataset.id.startsWith('local:') && !state.messages.some((y) => y.id === x.dataset.id) && x.lastChild.textContent === m.text);
        if (el) el.dataset.id = m.id;
      }
      el = el || bubble(m);
      used.add(el);
      return el;
    });
    const prefix = els.slice(0, existing.length);
    const added = els.length > existing.length;
    if (prefix.length === existing.length && prefix.every((el, i) => el === existing[i])) {
      if (added) thread.append(...els.slice(existing.length));
    } else {
      thread.replaceChildren(...els);
    }
    if (added) thread.scrollTo({ top: thread.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  let limitTimer = null;
  function render() {
    const now = Date.now();
    const v = view(state, now);
    const open = state.panel === 'open';
    root.hidden = false;
    panel.hidden = !open;
    launcher.setAttribute('aria-expanded', String(open));
    badge.textContent = state.unread > 0 ? String(state.unread) : '';
    badge.hidden = state.unread === 0;
    if (state.unread > 0) launcher.setAttribute('aria-label', launcherLabel(state.launcherLabel, state.unread));
    else launcher.removeAttribute('aria-label');
    identify.hidden = state.phase !== 'identify' || unavailable;
    thread.hidden = state.phase !== 'thread';
    compose.hidden = state.phase !== 'thread' || unavailable;
    sendBtn.disabled = !canSend(state, now) || busy;
    area.disabled = v === 'limited';
    const msg = unavailable ? COPY.unavailable
      : v === 'limited' ? COPY.limited
      : sendError ? sendError
      : v === 'waiting' ? COPY.waiting
      : v === 'email' ? COPY.email(state)
      : '';
    if (note.textContent !== msg) note.textContent = msg;
    renderThread();
    clearTimeout(limitTimer);
    if (v === 'limited') limitTimer = setTimeout(render, state.limitedUntil - now + 50);
  }

  // Polling (cadence and when to stop: keepPolling/pollCadence in the core). Only an active
  // poll (panel open, tab visible) sends seen=1, which is what marks replies delivered and the
  // visitor present on the CRM; background polls just fetch, so a reply read in a closed panel
  // still goes by email and /close's backdate holds.
  let timer = null;
  let failures = 0;
  let inflight = false;
  let rerun = false;
  const active = () => state.panel === 'open' && document.visibilityState === 'visible';
  async function poll() {
    if (!state.session || unavailable) return;
    if (inflight) { rerun = true; return; } // e.g. opened mid-poll: run again (with seen=1) right after
    inflight = true;
    clearTimeout(timer);
    try {
      const q = { session: state.session, since: state.since || '' };
      if (active()) q.seen = '1';
      const j = await api(`/poll?${new URLSearchParams(q)}`);
      failures = 0;
      dispatch({ type: 'poll', messages: j.messages || [], status: j.status, handoff: j.handoff, since: j.since });
    } catch (err) {
      if (err.message !== 'unknown') failures += 1;
    }
    inflight = false;
    if (rerun) { rerun = false; poll(); } else schedule();
  }
  function schedule() {
    clearTimeout(timer);
    if (inflight || !state.session || unavailable || !keepPolling(state, Date.now(), active())) return;
    timer = setTimeout(poll, pollCadence(active(), failures));
  }
  // Back on the tab with the panel open: catch up now (seen=1); otherwise re-pick the cadence.
  document.addEventListener('visibilitychange', () => (active() ? poll() : schedule()));

  const focusables = () => [...panel.querySelectorAll('button, input, textarea')]
    .filter((el) => !el.disabled && !el.closest('[hidden]'));
  function openPanel() {
    dispatch({ type: 'open' });
    poll(); // always once on open, then the open cadence
    // Focus the field to fill in; if it's hidden or disabled, the close button, so Escape still works.
    const field = identify.hidden ? area : identify.querySelector('input');
    (field.disabled || field.closest('[hidden]') ? closeBtn : field).focus();
  }
  function closePanel() {
    dispatch({ type: 'close' });
    schedule();
    launcher.focus();
  }
  launcher.addEventListener('click', () => (state.panel === 'open' ? closePanel() : openPanel()));
  closeBtn.addEventListener('click', () => {
    // The visitor closed the chat on purpose: tell the CRM so an unread reply goes by email sooner.
    if (state.session && !unavailable) {
      fetch(`${API}/close`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ session: state.session }),
      }).catch(() => {});
    }
    closePanel();
  });
  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closePanel(); return; }
    if (e.key !== 'Tab') return;
    // Keep Tab inside the open panel; Escape is the way out.
    const els = focusables();
    if (!els.length) return;
    const first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  identify.addEventListener('submit', (e) => {
    e.preventDefault();
    const f = new FormData(identify);
    dispatch({ type: 'identify', name: String(f.get('name') || ''), email: String(f.get('email') || '') });
    area.focus();
  });

  area.addEventListener('keydown', (e) => {
    // Enter sends; Shift+Enter starts a new line.
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); compose.requestSubmit(); }
  });

  compose.addEventListener('submit', async (e) => {
    e.preventDefault();
    const text = area.value.trim();
    if (!text || busy || !canSend(state, Date.now())) return;
    const at = new Date().toISOString();
    busy = true;
    sendError = '';
    compose.reset();
    dispatch({ type: 'sent', text, at });
    try {
      if (!state.session) {
        const j = await api('/start', {
          name: state.name, email: state.email, message: text,
          pageUrl: location.href, pageTitle: root.dataset.pageTitle,
          productHandle: root.dataset.productHandle || undefined,
        });
        dispatch({ type: 'started', session: j.session, conversationId: j.conversationId });
      } else {
        await api('/message', { session: state.session, message: text });
      }
      busy = false;
      render();
      poll();
    } catch (err) {
      // Spec: a failed send keeps the text in the composer so the visitor can retry.
      busy = false;
      dispatch({ type: 'unsent', at });
      area.value = text;
      if (err.message !== 'limited' && err.message !== 'unavailable') sendError = COPY.failed;
      render();
    }
  });

  render();
  if (state.session) poll();
}
